import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import {
  createBulkTimeEntry,
  createTimeEntry,
  editTimeEntry,
  fetchMemberTimeEntries,
  removeTimeEntry, 
  removeTimeEntryGroup,
} from 'api/timeEntries';
import { AppDispatch, RootState } from 'app/store';
import {
  IAddBulkTimeEntryData,
  IAddTimeEntryData,
  IEditTimeEntryData,
  ITimeEntryData,
} from './timeEntriesTypes';
import { MyKnownError, thunkErrorHandler } from 'features/thunkError';
import { IGetMemberEventsData, ICurrentCalendarParams } from 'features/calendarInfo/calendarInfoTypes';

export const getMemberTimeEntries = createAsyncThunk<
  { timeEntries: ITimeEntryData[]; params: ICurrentCalendarParams },
  IGetMemberEventsData,
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('@@timeEntries/getMemberTimeEntries', async (data, thunkApi) => {
  try {
    const res = await fetchMemberTimeEntries(data);
    const currentParams = thunkApi.getState().calendarInfo.currentCalendarParams;
    return { timeEntries: res.data, params: currentParams };
  } catch (err) {
    return thunkErrorHandler(err as MyKnownError, thunkApi.rejectWithValue);
  }
});

export const addTimeEntry = createAsyncThunk<
  void,
  IAddTimeEntryData,
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string | unknown;
  }
>('@@timeEntries/addTimeEntry', async (data, thunkApi) => {
  try {
    const res = await createTimeEntry(data);
    if (res.status === 200) {
      const currentParams =
        thunkApi.getState().calendarInfo.currentCalendarParams;
      thunkApi.dispatch(getMemberTimeEntries(currentParams));
      toast.success('Time entry created.');
    }
  } catch (err) {
    return thunkErrorHandler(err as MyKnownError, thunkApi.rejectWithValue);
  }
});

export const addBulkTimeEntry = createAsyncThunk<
  void,
  IAddBulkTimeEntryData,
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string | unknown;
  }
>('@@timeEntries/addBulkTimeEntry', async (data, thunkApi) => {
  try {
    const res = await createBulkTimeEntry(data);
    if (res.status === 200) {
      const currentParams =
        thunkApi.getState().calendarInfo.currentCalendarParams;
      thunkApi.dispatch(getMemberTimeEntries(currentParams));
      toast.success('Time entries created.');
    }
  } catch (err) {
    return thunkErrorHandler(err as MyKnownError, thunkApi.rejectWithValue);
  }
});

export const changeTimeEntry = createAsyncThunk<
  void,
  IEditTimeEntryData,
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string | unknown;
  }
>('@@timeEntries/changeTimeEntry', async (data, thunkApi) => {
  try {
    const res = await editTimeEntry(data);
    if (res.status >= 200 && res.status < 300) {
      const currentParams =
        thunkApi.getState().calendarInfo.currentCalendarParams;
      thunkApi.dispatch(getMemberTimeEntries(currentParams));
      toast.success('Time entry successfully updated.');
    }
  } catch (err) {
    return thunkErrorHandler(err as MyKnownError, thunkApi.rejectWithValue);
  }
});

export const deleteTimeEntry = createAsyncThunk<
  void,
  number,
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string | unknown;
  }
>('@@timeEntries/deleteTimeEntry', async (timeEntryId, thunkApi) => {
  try {
    const res = await removeTimeEntry(timeEntryId);
    if (res.status === 204) {
      const currentParams =
        thunkApi.getState().calendarInfo.currentCalendarParams;
      thunkApi.dispatch(getMemberTimeEntries(currentParams));
      toast.success('Time entry deleted.');
    }
  } catch (err) {
    return thunkErrorHandler(err as MyKnownError, thunkApi.rejectWithValue);
  }
});

export const deleteTimeEntryGroup = createAsyncThunk<
  void,
  string,
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string | unknown;
  }
>('@@timeEntries/deleteTimeEntryGroup', async (groupId, thunkApi) => {
  try {
    const res = await removeTimeEntryGroup(groupId);
    if (res.status === 204) {
      const currentParams =
        thunkApi.getState().calendarInfo.currentCalendarParams;
      thunkApi.dispatch(getMemberTimeEntries(currentParams));
      toast.success('Group deleted.');
    }
  } catch (err) {
    return thunkErrorHandler(err as MyKnownError, thunkApi.rejectWithValue);
  }
});